"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Cpu,
  Sparkles,
  Target,
  Shield,
  Brain,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  Activity,
  Lock,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "9", suffix: "", label: "Proprietary AI Platforms", sub: "Built in-house over 15+ years", accent: "cyan" },
  { metric: "0", suffix: "", label: "Third-Party Dependencies", sub: "Zero external dependencies", accent: "emerald" },
  { metric: "89", suffix: "%", label: "Crisis Prediction", sub: "Verified accuracy", accent: "purple" },
  { metric: "87", suffix: "%", label: "Booth-Level Accuracy", sub: "Ground-truth intelligence", accent: "amber-gold" },
];

const DIMENSIONS = [
  { title: "Transparency", body: "Every AI recommendation includes reasoning, data sources, and confidence levels. No black-box decisions at sovereign scale. Uncertainty is quantified, not assumed." },
  { title: "Accountability", body: "Human judgment remains paramount. AI systems augment decision-making; they never replace it. AI generates recommendations. Humans make decisions. The separation is structural." },
  { title: "Purpose Limitation", body: "Every engagement serves constructive outcomes. Projects with questionable motives are declined. The ethical boundary is non-negotiable and embedded into the intake process." },
  { title: "Data Sovereignty", body: "Sovereign-grade encryption protecting the most sensitive information. NIST post-quantum standards. Seven-layer defense-in-depth. Air-gap deployment capability. Hardware-accelerated encryption at 100 Gbps." },
  { title: "Outcome Verification", body: "Every claim is grounded in verified operational data — not projections, not targets, results. Verified outcomes from sovereign-scale deployments." },
];

const PLATFORMS = [
  { name: "Neural Command Interface", function: "Ecosystem orchestrator — unifies all platforms into a single operational fabric", accent: "red" },
  { name: "AI Digital Intelligence", function: "Predictive analytics — 89% crisis prediction, sub-60-second alerts", accent: "purple" },
  { name: "Autonomous Perception", function: "Narrative engineering at continental scale with organic authenticity", accent: "cyan" },
  { name: "Content Distribution Network", function: "1,000+ syndication points with 340% reach increase", accent: "emerald" },
  { name: "Ground-Truth Intelligence", function: "Booth-level voter prediction at 87% accuracy", accent: "amber-gold" },
  { name: "Governance Neural Hub", function: "129 capabilities across five governance pillars", accent: "denim" },
  { name: "Crisis Transformation Engine", function: "384x to 1,416x faster than traditional approaches", accent: "lime" },
  { name: "Sovereign Security System", function: "99.9999% uptime, zero breaches across 15+ years", accent: "coral" },
  { name: "eGovernance Platform", function: "127 modules serving 120M citizens in single deployments", accent: "amber-gold" },
];

const TRENDS = [
  { num: "01", title: "AI Governance as Differentiator", body: "AI governance will become the primary differentiator between organizations that command intelligence and organizations that are commanded by it. The era of unmonitored, unauditable AI is ending.", accent: "red" },
  { num: "02", title: "Sovereign AI as National Security", body: "Sovereign AI — the capacity to build, deploy, and govern AI systems with complete operational independence — will become a matter of national security.", accent: "purple" },
  { num: "03", title: "Integrated Intelligence Architectures", body: "The era of point solutions is giving way to integrated intelligence architectures. The organization that operates multiple platforms in concert possesses a capability that point solutions cannot replicate.", accent: "cyan" },
];

const APPLICATIONS = [
  { title: "Sovereign Application", body: "Governments, defense, intelligence, and public administration. Population-scale deployments serving 120 million citizens. Federal government ROI: 620%. Policy implementation rates improving from 34% to 94%.", accent: "red" },
  { title: "Enterprise Application", body: "Fortune 500 corporations. Enterprise Intelligence Layer deployed at 50+ Fortune 500 companies. 450% financial ROI with $12M in annual savings. 1,100% ROI in healthcare. The AI governance discipline that sovereign operations demand.", accent: "purple" },
  { title: "Future Vision", body: "The future belongs to those who govern intelligence. Not those who merely possess it. The architecture that fifteen-plus years of sovereign-scale deployment have proven: AI governed with clinical precision, deployed with sovereign-grade security.", accent: "cyan" },
];

export default function AITechnologyFuturePage() {
  return (
    <>
      <PageHero
        icon={Cpu}
        overline="AI, Technology & The Future"
        overlineClassName="text-cyan"
        title={
          <>
            Governing Intelligence <span className="text-gradient-premium">at Sovereign Scale.</span>
          </>
        }
        subtitle="The future of AI is not a technology question. It is a governance question. The organizations and governments that survive the AI revolution will not be the ones that adopt the most tools. They will be the ones that govern intelligence with the precision, accountability, and ethical discipline that sovereign-scale operations demand."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "AI, Technology & The Future" }]}
        badge="The operator who has been governing AI for 15+ years"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Sparkles className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The AI Imperative</span>
            </div>
            <h2 className="text-display-md mb-5">
              The organizations that govern AI will command outcomes. The rest will be commanded by it.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The age of artificial intelligence is not approaching. It has arrived. Every government, every corporation, every institution on earth is already operating in an AI-saturated environment — whether they acknowledge it or not. The question is not whether to adopt AI. The question is whether to govern it.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The AI platforms Dr. Jyoti Kush commands were not purchased, licensed, or adopted from third-party vendors. They were built — from the ground up, over a decade, on infrastructure that took years to construct. Every line of code proprietary. Every algorithm auditable. Every decision traceable to data, methodology, and human judgment.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Brain className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Proprietary AI Ecosystem</span>
          </div>
          <h2 className="text-display-md mb-4">An arsenal built, not bought.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Nine proprietary AI platforms constitute the most comprehensive demonstration of sovereign-scale artificial intelligence in existence. Every platform built in-house. None can be replicated without the same decade of investment.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          {PLATFORMS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-caption font-bold ${a.text} opacity-50 mb-2`}>Platform {`0${i + 1}`}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.name}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.function}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Shield className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Ethical Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Sovereign-grade ethics as operational discipline.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            When AI systems process the most sensitive data on earth, the ethical dimension is not a compliance checkbox. It is an operational imperative.
          </p>
        </div>
        <div className="space-y-3">
          {DIMENSIONS.map((d, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-body-md font-bold text-white">{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{d.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <TrendingUp className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Future Trajectory</span>
          </div>
          <h2 className="text-display-md mb-4">Where AI governance is heading.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The trajectory points toward three defining trends. Dr. Jyoti Kush has been operating at this intersection for over a decade.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TRENDS.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{t.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{t.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{t.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Target className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Three Application Domains</span>
          </div>
          <h2 className="text-display-md mb-4">From sovereign operations to enterprise intelligence.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The AI governance discipline adapts to the altitude of every engagement — from national-scale deployments to Fortune 500 enterprise operations.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {APPLICATIONS.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${ac.text}`}>{a.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="ai-capability-grid" ariaLabel="AI Capability Grid">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Cpu className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The AI Capability Grid</span>
          </div>
          <h2 className="text-display-md mb-4">Eight capability domains. One sovereign AI fabric.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature compilation. Each domain represents a distinct AI capability deployed at sovereign scale. The domains are not parallel — they interlock, each providing the foundation for the others.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Predictive Intelligence", body: "Eighty-nine percent crisis prediction accuracy. Sub-sixty-second alert latency. The capability that enables preemptive action rather than reactive damage control.", icon: <Brain className="size-5" />, accent: "red", span: "lg" },
            { title: "Narrative Generation", body: "Ninety-five to ninety-eight percent organic authenticity. One thousand to one hundred thousand-plus variants per campaign. Four core engines running in parallel.", icon: <Sparkles className="size-5" />, accent: "purple", span: "sm" },
            { title: "Booth-Level Forecasting", body: "Eighty-seven percent accuracy — twenty-six points above digital-only methods. Eight hundred forty-seven-plus electoral cycles informing the prediction.", icon: <Target className="size-5" />, accent: "cyan", span: "sm" },
            { title: "Sentiment Recovery Modeling", body: "Five recovery curves. The right curve selected per crisis. The wrong curve multiplies damage by orders of magnitude.", icon: <Activity className="size-5" />, accent: "emerald", span: "sm" },
            { title: "Multi-Language Operations", body: "Fifty-plus languages. Cultural calibration embedded in every output. The capacity to maintain AI operations across linguistic and cultural boundaries.", icon: <BookOpen className="size-5" />, accent: "amber-gold", span: "sm" },
            { title: "Crisis Response Generation", body: "Natural language generation calibrated to brand voice and crisis type. Sub-sixty-second draft generation with verification checkpoints.", icon: <Shield className="size-5" />, accent: "denim", span: "wide" },
            { title: "Influence Network Mapping", body: "Eight hundred fifty thousand-plus profiled advocates. Network analysis, node identification, amplification pathway mapping.", icon: <Layers className="size-5" />, accent: "coral", span: "sm" },
            { title: "Sovereign Encryption Layer", body: "Military-grade encryption, zero-trust architecture, post-quantum cryptography. The layer that protects every other capability.", icon: <Lock className="size-5" />, accent: "lime", span: "sm" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Brain className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Doctrine — AI Governance as Clinical Discipline</span>
          </div>
          <h2 className="text-display-md mb-4">Six principles for the era of sovereign AI.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The doctrine that has governed AI deployments for fifteen-plus years. Six principles that translate clinical discipline into AI governance. The principles that distinguish organizations that command AI from organizations commanded by it.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "Transparency", body: "Every AI recommendation includes reasoning, data sources, and confidence levels. No black-box decisions at sovereign scale. Uncertainty is quantified, not assumed.", icon: <Brain className="size-5" />, accent: "red" },
            { title: "Accountability", body: "Human judgment remains paramount. AI systems augment decision-making. AI generates recommendations. Humans make decisions. The separation is structural.", icon: <Target className="size-5" />, accent: "purple" },
            { title: "Purpose Limitation", body: "Every engagement serves constructive outcomes. Projects with questionable motives are declined. The ethical boundary is non-negotiable and embedded into the intake process.", icon: <Shield className="size-5" />, accent: "cyan" },
            { title: "Data Sovereignty", body: "Sovereign-grade encryption protecting the most sensitive information. NIST post-quantum standards. Seven-layer defense-in-depth. Air-gap deployment capability.", icon: <Lock className="size-5" />, accent: "emerald" },
            { title: "Outcome Verification", body: "Every claim is grounded in verified operational data — not projections, not targets, results. Verified outcomes from sovereign-scale deployments.", icon: <Activity className="size-5" />, accent: "amber-gold" },
            { title: "Continuous Refinement", body: "Every engagement makes the next engagement smarter. The system that enters the next deployment is sharper than the one that entered the last. The loop never closes — it accelerates.", icon: <TrendingUp className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Activity className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Sovereign AI Performance Record</span>
          </div>
          <h2 className="text-display-md mb-4">Fifteen years. Nine platforms. One verified record.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data from sovereign-scale deployments. The future of AI governance is not theoretical. It is the verified record of an organization that has been governing AI at the highest level for over a decade.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 9, label: "Proprietary AI Platforms", description: "Built in-house over fifteen-plus years", accent: "cyan" },
            { value: 0, label: "Third-Party Dependencies", description: "Zero external dependencies", accent: "emerald" },
            { value: 89, suffix: "%", label: "Crisis Prediction", description: "Verified accuracy across deployments", accent: "purple" },
            { value: 87, suffix: "%", label: "Booth-Level Accuracy", description: "Twenty-six points above digital-only methods", accent: "amber-gold" },
            { value: 95, suffix: "-98", prefix: "", label: "Organic Authenticity %", description: "Content statistically indistinguishable from organic spread", accent: "red" },
            { value: 10000, suffix: "+", label: "Requests Per Second", description: "Sustained processing capacity", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage sovereign-grade AI governance.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The AI governance practitioner the world needs. Not theory — verified sovereign-scale operations. Keynotes, advisory, and consulting engagements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Cpu, accent: "cyan" },
          { title: "Digital Transformation", description: "Engineering the digital future. The operating system for statecraft at national scale.", href: "/expertise/digital-transformation/", icon: TrendingUp, accent: "purple" },
          { title: "Medicine Meets Technology", description: "The physician&apos;s discipline for the digital age. Clinical rigor applied to sovereign systems.", href: "/expertise/medicine-meets-technology/", icon: Brain, accent: "emerald" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
